import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Inbox, Handshake, Newspaper, FileEdit, Building2, Mail, MousePointerClick, Link2, TrendingUp } from "lucide-react";

const CARDS = [
  { key: "leads", label: "Total Leads", icon: Inbox },
  { key: "partner_applications", label: "Partner Applications", icon: Handshake },
  { key: "published_articles", label: "Published Articles", icon: Newspaper },
  { key: "draft_articles", label: "Draft Articles", icon: FileEdit },
  { key: "brokers", label: "Broker Partners", icon: Building2 },
  { key: "subscribers", label: "Subscribers", icon: Mail },
];

export default function Dashboard() {
  const { data } = useQuery({ queryKey: ["admin-dash"], queryFn: async () => (await api.get("/admin/dashboard")).data });
  const { data: analytics } = useQuery({ queryKey: ["admin-analytics"], queryFn: async () => (await api.get("/admin/analytics")).data });
  const stats = data?.stats || {};

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-ink">Dashboard</h1>
      <p className="mt-1 text-sm text-subtle">Overview of leads, applications, content and marketing performance.</p>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((c) => (
          <div key={c.key} className="rounded-xl border border-hair bg-white p-5 shadow-sm" data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-canvas text-finblue"><c.icon className="h-5 w-5" strokeWidth={1.5} /></span>
              <span className="font-heading text-3xl font-bold text-navy">{stats[c.key] ?? "—"}</span>
            </div>
            <p className="mt-3 text-sm font-medium text-subtle">{c.label}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-10 font-heading text-lg font-semibold text-ink">Marketing Performance</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="analytics-widgets">
        <Mini label="Broker Clicks" value={analytics?.broker_clicks_total} icon={MousePointerClick} />
        <Mini label="Campaign Clicks" value={analytics?.campaign_clicks_total} icon={Link2} />
        <Mini label="Signups (7d)" value={analytics?.signups_7d} icon={Mail} />
        <Mini label="Leads (7d)" value={analytics?.leads_7d} icon={TrendingUp} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <RankList title="Top Brokers by Clicks" rows={analytics?.top_brokers} label={(r) => r.name} value={(r) => r.clicks} empty="No broker clicks tracked yet." />
        <RankList title="Top Campaigns by Clicks" rows={analytics?.top_campaigns} label={(r) => r.name} value={(r) => r.clicks} empty="No campaigns yet." />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <RecentList title="Recent Enquiries" rows={data?.recent_contacts} render={(r) => (<><p className="font-medium text-ink">{r.full_name}</p><p className="text-xs text-subtle">{r.interest} · {r.email}</p></>)} />
        <RecentList title="Recent Applications" rows={data?.recent_applications} render={(r) => (<><p className="font-medium text-ink">{r.full_name}</p><p className="text-xs text-subtle">{r.business_type || "—"} · {r.email}</p></>)} />
      </div>
    </div>
  );
}

function Mini({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-hair bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy/5 text-navy"><Icon className="h-4.5 w-4.5 h-5 w-5" strokeWidth={1.5} /></span>
        <span className="font-heading text-2xl font-bold text-navy">{value ?? "—"}</span>
      </div>
      <p className="mt-3 text-sm font-medium text-subtle">{label}</p>
    </div>
  );
}

function RankList({ title, rows, label, value, empty }) {
  return (
    <div className="rounded-xl border border-hair bg-white p-5 shadow-sm">
      <h2 className="font-heading text-lg font-semibold text-ink">{title}</h2>
      <div className="mt-4 space-y-2.5">
        {(rows || []).length === 0 && <p className="text-sm text-subtle">{empty}</p>}
        {(rows || []).map((r, i) => (
          <div key={i} className="flex items-center justify-between border-b border-hair pb-2 last:border-0">
            <span className="text-sm text-ink">{label(r)}</span>
            <span className="font-heading text-sm font-bold text-navy">{value(r)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecentList({ title, rows, render }) {
  return (
    <div className="rounded-xl border border-hair bg-white p-5 shadow-sm">
      <h2 className="font-heading text-lg font-semibold text-ink">{title}</h2>
      <div className="mt-4 space-y-3">
        {(rows || []).length === 0 && <p className="text-sm text-subtle">No records yet.</p>}
        {(rows || []).map((r) => <div key={r.id} className="border-b border-hair pb-3 last:border-0">{render(r)}</div>)}
      </div>
    </div>
  );
}
